import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import {
    LayoutDashboard, FileText, Layers, Wrench, FolderOpen, BookOpen, Repeat,
    Inbox, Image as ImageIcon, Settings, LogOut, Menu, ExternalLink, Users
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Toaster } from '@/components/ui/sonner';
import SEO from '@/components/SEO';

const NAV = [
    { href: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { href: '/admin/pages', label: 'Stranice', icon: FileText },
    { href: '/admin/services', label: 'Usluge', icon: Wrench },
    { href: '/admin/projects', label: 'Reference', icon: FolderOpen },
    { href: '/admin/blog', label: 'Blog', icon: BookOpen },
    { href: '/admin/redirects', label: 'Redirecti', icon: Repeat },
    { href: '/admin/messages', label: 'Poruke', icon: Inbox },
    { href: '/admin/media', label: 'Mediji', icon: ImageIcon },
    { href: '/admin/users', label: 'Korisnici', icon: Users, adminOnly: true },
    { href: '/admin/settings', label: 'Postavke', icon: Settings, adminOnly: true },
];

export default function AdminLayout() {
    const { user, loading, logout } = useAuth();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-[#0B0C0E] text-white">Učitavanje…</div>;
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    const isActive = (item) => item.exact ? location.pathname === item.href : location.pathname.startsWith(item.href);

    const isAdmin = user?.role === 'admin';
    const visibleNav = NAV.filter((item) => !item.adminOnly || isAdmin);

    const sidebarContent = (
        <>
            <div className="px-4 pt-4 pb-6">
                <Link to="/admin" className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-[#B08D57] flex items-center justify-center text-white font-bold">Š</div>
                    <div>
                        <p className="text-sm font-semibold text-white">Škobić Keramika</p>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/50">Admin Panel</p>
                    </div>
                </Link>
            </div>
            <nav className="flex-1 px-2 space-y-0.5" data-testid="admin-sidebar">
                {visibleNav.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item);
                    return (
                        <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                                active
                                    ? 'bg-[#161A21] text-white'
                                    : 'text-white/70 hover:bg-[#161A21] hover:text-white'
                            )}
                            data-testid={`admin-nav-${item.label.toLowerCase()}`}
                        >
                            <Icon className="h-4 w-4" />
                            {item.label}
                        </Link>
                    );
                })}
            </nav>
            <div className="px-2 pb-4 pt-4 border-t border-[#232A36] mt-4">
                <Link
                    to="/"
                    target="_blank"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-[#161A21] hover:text-white"
                >
                    <ExternalLink className="h-4 w-4" />
                    Otvori sajt
                </Link>
                <button
                    onClick={logout}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-[#161A21] hover:text-white"
                    data-testid="admin-logout-button"
                >
                    <LogOut className="h-4 w-4" />
                    Odjava
                </button>
                <div className="px-3 mt-3 text-xs text-white/50">
                    {user.email}
                </div>
            </div>
        </>
    );

    return (
        <div className="min-h-screen bg-[#0B0C0E] text-[#F5F7FA] dark">
            <SEO title="Admin | Škobić Keramika" noindex nofollow />
            <div className="flex min-h-screen">
                {/* Desktop sidebar */}
                <aside className="hidden lg:flex flex-col w-64 bg-[#0B0C0E] border-r border-[#232A36] fixed h-screen">
                    {sidebarContent}
                </aside>

                {/* Mobile sidebar */}
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetContent side="left" className="w-72 p-0 bg-[#0B0C0E] border-[#232A36] text-white">
                        <SheetTitle className="sr-only">Admin navigacija</SheetTitle>
                        <div className="flex flex-col h-full">{sidebarContent}</div>
                    </SheetContent>
                </Sheet>

                {/* Main content */}
                <div className="flex-1 lg:ml-64">
                    <header className="sticky top-0 z-30 flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 h-14 bg-[#0B0C0E]/95 backdrop-blur border-b border-[#232A36]">
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-[#161A21]"><Menu className="h-5 w-5" /></Button>
                            </SheetTrigger>
                        </Sheet>
                        <div className="text-sm text-white/70">
                            {NAV.find(isActive)?.label || 'Admin'}
                        </div>
                    </header>
                    <main className="p-4 sm:p-6 lg:p-8">
                        <Outlet />
                    </main>
                </div>
            </div>
            <Toaster theme="dark" position="top-right" richColors />
        </div>
    );
}
